import { TabContext, TabList } from "@mui/lab";
import { Box, Button, InputAdornment, Tab, TextField, ThemeProvider } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useCallback, useEffect, useMemo, useState } from "react";
import { muiTheme } from "system/styles/muiTheme";
import LoadingPage from "./system/LoadingPage";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import useManageAlertApi from "system/hooks/use-api-manage-alert";
import { AudienceCategory, IAlertFormData, SeverityType, SourceType } from "system/types/interfaces/IAlertFormData ";
import CreateAlert from "../modules/manage-alert/components/createAlert/CreateAlert";
import { AlertDetailDrawer } from "../modules/manage-alert/components/alertNotification/AlertDetailDrawer";
import { setUnreadCount } from "system/store/slices/alert-slice";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import { sanitizeHtml } from "system/utils/sanitizeHtml";

const ManageAlertsPage = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [alertDialogVisible, setAlertDialogVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshAlerts, setRefreshAlerts] = useState(() => Math.random());
  const { fetchAlerts, disableAlert } = useManageAlertApi();
  const [alerts, setAlerts] = useState<IAlertFormData[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<IAlertFormData | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editAlert, setEditAlert] = useState<IAlertFormData | null>(null);

  const [severityTypes, setSeverityTypes] = useState<SeverityType[]>([]);
  const [audienceCategories, setAudienceCategories] = useState<AudienceCategory[]>([]);
  const [sourceTypes, setSourceTypes] = useState<SourceType[]>([]);
  const api = useManageAlertApi();

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [sev, aud, src] = await Promise.all([api.getAlertSeverityTypes(), api.getAlertAudienceCategory(), api.getAlertSourceTypes()]);
        setSeverityTypes(Array.isArray(sev) ? sev : sev?.value ?? []);
        setAudienceCategories(Array.isArray(aud) ? aud : aud?.value ?? []);
        setSourceTypes(Array.isArray(src) ? src : src?.value ?? []);
      } catch (err) {
        console.error("Failed to load lookups", err);
      }
    };

    loadLookups();
  }, [api]);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  }, []);

  const extractErrorMessage = (err: any): string => {
    if (err?.response?.data?.Error) {
      return err.response.data.Error;
    }
    return "An unexpected error occurred.";
  };

  const handleSaveAlert = (savedAlert: IAlertFormData) => {
    setAlertDialogVisible(false);
    setRefreshAlerts(Math.random());
  };

  const loadAlerts = useCallback(async () => {
    try {
      const alertsFromApi = await fetchAlerts();
      setAlerts(alertsFromApi?.data);
      dispatch(setUnreadCount(alertsFromApi?.unreadCount));
    } catch (error) {
      setErrorMessage(extractErrorMessage(error));
      console.error("Failed to fetch alerts", error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchAlerts]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts, refreshAlerts]);

  const handleTitleClick = useCallback((params: any) => {
    const alert = params.row;

    setSelectedAlert({
      id: alert.id,
      alertTitle: alert.alertTitle,
      description: alert.description,
      severity: alert.severity,
      audience: alert.audience,
      status: alert.status,
      startDate: alert.startDate,
      endDate: alert.endDate,
    });

    setDrawerOpen(true);
  }, []);

  const handleEditFromDrawer = (alert: IAlertFormData) => {
    const cleanAudience = alert.audience
      ? alert.audience
          .flatMap((a) => a.split(","))
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

    const finalAudience = cleanAudience.includes("All Customers") ? ["All Customers"] : cleanAudience;

    setEditAlert({
      ...alert,
      audience: finalAudience,
    });

    setDrawerOpen(false);
    setAlertDialogVisible(true);
  };

  const filteredAlerts = useMemo(() => {
    if (!searchText && activeTab === "all") return alerts;

    const lowerQuery = searchText.toLowerCase();
    const severityMap = {
      info: "Info",
      warning: "Warning",
      urgent: "Urgent",
    };

    return alerts.filter((alert) => {
      const matchesQuery =
        searchText === "" || alert.alertTitle.toLowerCase().includes(lowerQuery) || alert.description.toLowerCase().includes(lowerQuery);

      const matchesSeverity = activeTab === "all" || alert.severity === severityMap[activeTab as keyof typeof severityMap];

      return matchesQuery && matchesSeverity;
    });
  }, [searchText, alerts, activeTab]);

  const alertColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: "alertTitle",
        headerName: "Alert Title",
        flex: 1,
        renderCell: (params) => (
          <span
            onClick={() => handleTitleClick(params)}
            style={{
              textDecoration: "underline",
              cursor: "pointer",
              color: "#42a5f5",
            }}
          >
            {params.value}
          </span>
        ),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1,
        renderCell: (params) => (
          <div
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(params.value) }}
          />
        ),
      },

      {
        field: "severity",
        headerName: "Severity",
        width: 140,
        renderCell: (params) => {
          const severityIcons = {
            Info: <InfoIcon sx={{ color: "#2196f3" }} />,
            Warning: <WarningAmberIcon sx={{ color: "#ff9800" }} />,
            Urgent: <ReportProblemIcon sx={{ color: "#f44336" }} />,
          };

          return (
            <Box display="flex" alignItems="center" gap={1}>
              {severityIcons[params.value as keyof typeof severityIcons] || params.value}
              {params.value}
            </Box>
          );
        },
      },

      { field: "audience", headerName: "Audience", width: 120 },
      {
        field: "startDate",
        headerName: "Effective Date From",
        width: 180,
        renderCell: (params) => {
          if (!params.value) return "N/A";
          const [datePart, timePart] = params.value.split(" ");
          const utcDate = new Date(`${datePart}T${timePart}Z`);
          const localDate = new Date(utcDate);
          return format(localDate, "PPpp");
        },
      },
      {
        field: "endDate",
        headerName: "Effective Date To",
        width: 180,
        renderCell: (params) => {
          if (!params.value) return "N/A";
          const [datePart, timePart] = params.value.split(" ");
          const utcDate = new Date(`${datePart}T${timePart}Z`);
          const localDate = new Date(utcDate);
          return format(localDate, "PPpp");
        },
      },
      { field: "status", headerName: "Status", width: 100 },
    ],
    [handleTitleClick]
  );

  const handleDisableAlert = useCallback(
    async (alertId: number) => {
      try {
        await disableAlert(alertId);
        setRefreshAlerts(Math.random());
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
        console.error("Failed to disable alert", err);
      }
    },
    [disableAlert]
  );

  const dataGridStyles = useMemo(
    () => ({
      "& .MuiDataGrid-cell": {
        borderBottom: "1px solid #424242",
        backgroundColor: "#3c3c3c",
        color: "#ffffff",
      },
      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: "#1e1e1e",
        borderBottom: "1px solid #424242",
        color: "white",
      },
      "& .MuiDataGrid-footerContainer": {
        backgroundColor: "#1e1e1e",
        borderTop: "1px solid #424242",
        color: "white",
        justifyContent: "center",
      },
      "& .MuiTablePagination-toolbar": {
        color: "white",
        alignItems: "baseline",
        paddingTop: "10px",
      },
    }),
    []
  );

  return (
    <div className="alert-home">
      <div className="alert-title">
        <h2>Alerts</h2>
      </div>

      {isLoading ? (
        <LoadingPage />
      ) : (
        <TabContext value={activeTab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}></Box>

          <ThemeProvider theme={muiTheme}>
            <TabList onChange={handleTabChange} aria-label="alertTabs">
              <Tab label="All" value="all" sx={{ color: "white" }} />
              <Tab label="Urgent" value="urgent" sx={{ color: "white" }} />
              <Tab label="Warning" value="warning" sx={{ color: "white" }} />
              <Tab label="Info" value="info" sx={{ color: "white" }} />
            </TabList>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                mb: 1,
              }}
            >
              <Button
                className="alert-buttons"
                variant="contained"
                color="primary"
                size="small"
                endIcon={<AddIcon />}
                onClick={() => setAlertDialogVisible(true)}
              >
                Create Alert
              </Button>

              <CreateAlert
                key={editAlert?.id || "new"}
                open={alertDialogVisible}
                onClose={() => {
                  setAlertDialogVisible(false);
                  setEditAlert(null);
                }}
                onSave={(data) => {
                  handleSaveAlert(data);
                  setEditAlert(null);
                }}
                editData={editAlert}
                isEditMode={!!editAlert}
                setRefreshAlerts={setRefreshAlerts}
                severityTypes={severityTypes}
                audienceCategories={audienceCategories}
                sourceTypes={sourceTypes}
              />

              <TextField
                label="Search"
                variant="filled"
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ width: 300 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ height: "640px", width: "100%" }}>
              <DataGrid
                rows={filteredAlerts}
                columns={alertColumns}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10, page: 0 },
                  },
                }}
                pageSizeOptions={[10, 20, 50, 100]}
                disableRowSelectionOnClick
                sx={dataGridStyles}
              />

              {selectedAlert && (
                <AlertDetailDrawer
                  open={drawerOpen}
                  onClose={() => setDrawerOpen(false)}
                  alert={selectedAlert}
                  onDisable={handleDisableAlert}
                  onSaveAlert={(data) => {
                    console.log("Updated alert data:", data);
                  }}
                  setRefreshAlerts={setRefreshAlerts}
                  onEdit={handleEditFromDrawer}
                />
              )}
            </Box>
          </ThemeProvider>
        </TabContext>
      )}
    </div>
  );
};

export default ManageAlertsPage;
